import { AgreementListItem, IAgreement, IAgreementType } from "models/agreements";
import { ElectricityAgreement } from "models/electricity/agreement";
import React, { useContext, useState } from "react";
import {Link, useHistory} from 'react-router-dom';
import AgreementsService from "services/agreements.service";
import { AuthenticationContext, CustomerPermission } from "services/context/authentication.context";
import ElectricityAgreementService from "services/electricity/electricity-agreement.service";
import printJS from 'print-js';
import PDFPreviewModal from 'components/Modals/PDFPreviewModal/PDFPreviewModal';
import { IFilePreview } from "common/interfaces/IFilePreview";
import { ChargingAgreement } from "models/charging/agreement";
import ElectricityAgreementCard from "./ElectricityAgreementCard";
import OilAgreementCard from "./OilAgreementCard";
import ChargingAgreementCard from "./ChargingAgreementCard";
import ChargingAgreementService from "services/charging/charging-agreement.service";

interface AgreementListProps {
    agreements: AgreementListItem[];
    startSigning: (agreement: IAgreement) => void;
}

type FilePreviewPayload = {
    fileType: 'PDF' | 'BDOC',
    agreementType: IAgreementType
}

export default function AgreementList(props: AgreementListProps) {
    const history = useHistory();

    const { state, dispatch, hasPermission } = useContext(AuthenticationContext);

    const [isDownloading, setIsDownloading] = useState(false);

    const [filePreview, setFilePreview] = useState<IFilePreview<FilePreviewPayload> | null>();
    const [filePreviewLoading, setFilePreviewLoading] = useState<string>();

    const getAgreementPreview = async (agreement: ChargingAgreement | ElectricityAgreement | IAgreement) => {
        setFilePreviewLoading(agreement.contractNumber);

        let objectUrl = undefined;

        try{
            let fileType : 'PDF' | 'BDOC' = 'PDF';
            let agreementType;
            let fileName;

            if ("consumptionMarginal" in agreement) {
                fileName = `${agreement.contractNumber}.pdf`

                const res = await ElectricityAgreementService.getElectricityAgreementPdf(
                    agreement.id,
                    fileName
                );

                objectUrl = res.data;
                agreementType = IAgreementType.ELECTRICITY
            } else if("pricing" in agreement) {                
                const res = await ChargingAgreementService.getAgreementPdf(
                    agreement.id,
                    state.customerId!,
                    fileName
                );

                objectUrl = res.data;
                fileName = `${agreement.contractNumber}.pdf`
                agreementType = IAgreementType.CHARGING
            } else {
                const oilAgreement = agreement as IAgreement;

                const res = await AgreementsService.getAgreementFile(oilAgreement.id, 'PDF');
                objectUrl = res.data;
                agreementType = oilAgreement.type;
                fileType = oilAgreement.isSignedByCustomer || oilAgreement.isSignedByVendor ? 'BDOC' : 'PDF'
                fileName = agreement.fileName;
            }

            setFilePreview({
                id: agreement.id,
                objectUrl,
                fileName,
                payload: {
                    fileType,
                    agreementType
                }
            });

        }finally{
            setFilePreviewLoading(undefined);
        }
    }

    const downloadAgreement = async () => {
        if(!filePreview){
            return;
        }

        setIsDownloading(true);

        try {
            if (filePreview.payload?.agreementType === IAgreementType.ELECTRICITY) {
                await ElectricityAgreementService.getElectricityAgreementPdfWithDownload(
                    filePreview.id, 
                    filePreview.fileName
                );
            } else if(filePreview.payload?.agreementType === IAgreementType.CHARGING) {
                await ChargingAgreementService.getAgreementPdfWithDownload(
                    filePreview.id, 
                    state.customerId!,
                    filePreview.fileName
                );
            } else {
                await AgreementsService.getAgreementFileWithDownload(filePreview.id, filePreview.payload?.fileType!);
            }
        } catch (e) {
            console.error(e);
        }finally{
            setIsDownloading(false);
        }
    }

    const onChangePackage = (agreement: ElectricityAgreement): void => {
        dispatch({
            type: 'setChangePackage',
            value: agreement
        });

        history.push('/elekter/leping');
    }

    const onOpenOffer = (agreement: AgreementListItem): void => {
        if("pricing" in agreement){
            dispatch({
                type: 'setViewChargingOffer',
                value: agreement
            });
    
            history.push('/laadimine/leping');
        }
        if("consumptionMarginal" in agreement){
            history.push(`/elekter/vaata-pakkumist/${agreement.id}`);
        }
    }

    const isWebAdmin = hasPermission(CustomerPermission.WEB_ADMIN);

    if (!props.agreements.length) {
        return (
          <div className="no-agreements d-flex flex-column justify-content-center align-items-center bg-white p-5">
              <h1 className="mb-5 text-center">Hetkel sõlmitud lepingud puuduvad</h1>
              {
                isWebAdmin && (
                    <Link to="/elekter/leping" className="btn-lg btn-primary d-flex flex-row align-items-center">
                        <div className="icon--12 icon__plus icon__plus-white mr-2"/>
                        <span>
                                Sõlmi elektrileping
                            </span>
                    </Link>
                )
              }
          </div>
        )
    }

    return (
        <>
            <div className="d-flex flex-column">
                <div className="d-flex flex-row align-items-center justify-content-between mb-3">
                    <h2 className="my-0">Lepingud</h2>
                    {
                        isWebAdmin && (
                            <Link to="/elekter/leping" className="btn-lg btn-primary d-flex flex-row align-items-center">
                                <div className="icon--12 icon__plus icon__plus-white mr-2"/>
                                <span>
                                    Lisa uus elektrileping
                                </span>
                            </Link>
                        )
                    }
                </div>
                { props.agreements.map(a => {
                    switch (a.type) {
                        case IAgreementType.ELECTRICITY:
                            return (
                                <ElectricityAgreementCard 
                                    key={a.contractNumber}
                                    agreement={a as ElectricityAgreement}
                                    getFilePreview={() => getAgreementPreview(a)}
                                    isFilePreviewLoading={filePreviewLoading === a.contractNumber}
                                    changePackage={() => onChangePackage(a as ElectricityAgreement)}
                                    openOffer={() => onOpenOffer(a)}
                                />
                            );
                    
                        case IAgreementType.CHARGING:
                            return (
                                <ChargingAgreementCard 
                                    key={a.contractNumber}
                                    agreement={a as ChargingAgreement}
                                    getFilePreview={() => getAgreementPreview(a)}
                                    isFilePreviewLoading={filePreviewLoading === a.contractNumber}
                                    openOffer={() => onOpenOffer(a)}
                                />
                            );
                    
                        default:
                            return (
                                <OilAgreementCard
                                    key={a.contractNumber}
                                    agreement={a as IAgreement}
                                    getFilePreview={() => getAgreementPreview(a)}
                                    isFilePreviewLoading={filePreviewLoading === a.contractNumber}
                                    startSigning={() => props.startSigning(a as IAgreement)}
                                />
                            );
                    }
                })}
            </div>
            <PDFPreviewModal
                show={!!filePreview}
                onHide={() => setFilePreview(undefined)}
                onDownload={() => downloadAgreement()}
                isDownloading={isDownloading}
                onPrint={() => printJS({printable: filePreview?.objectUrl!, type: "pdf"})}
                pdfPreview={filePreview?.objectUrl}
            />
        </>
    );
}